import React, { useMemo } from 'react';
import { Tooltip } from 'antd';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router';

import DataTable from '../../../../../../shared/designSystem/components/DataTable';
import AttributionIcon, {
  resolveAttributionMethod,
} from '../../../../../../shared/designSystem/components/AttributionIcon';
import ImpressionSubTable from './ImpressionsTimeLine';
import ClicksSubTable from './ClicksTimeLine';
import TransactionImpressions from './transactionimpressions';
import { formatCurrency2SigFig } from '../../../../../../../core/utils/campaigns';

// =============================================================================
// TransactionsTable (V1) — migrated to the shared DataTable primitive. Same
// column model as before: Order / Last Impression (or Clicks for CPC) /
// Ordered on / Total / Attribution Method.
//
// Attribution icons match the V2 Transactions tab: Lucide Cookie /
// Fingerprint / Smartphone / Globe replace the V1 PNG sprite set
// (cnnaid.png / cnnaip.png / cnnafingerprint.png / globephone.svg). Mapping
// is documented in MEMORY.md under "Attribution method icons".
//
// Expanded row behavior preserved:
//   - Simplified reports -> <TransactionImpressions /> lazy-loader
//   - Full reports -> <ClicksSubTable /> for CPC or <ImpressionSubTable />
//     for everything else
// =============================================================================

// ATTRIBUTION_META + resolveAttributionMethod are now exported from the
// shared `AttributionIcon` primitive. Both V1 and V2 Transactions tables
// (plus any future attribution-aware surface) render the same mapping
// from a single source.

const TransactionsTable = ({
  transactionData,
  transactionTotalRecord,
  type,
  dateTrue,
  dateFilter,
  isSimplified,
  reportName,
  campaignOrderId,
  setCurrentIdx,
  loading,
  currentPage,
  setCurrentPage,
  publisherInfo,
}) => {
  const location = useLocation();
  const history = useHistory();
  const params = new URLSearchParams(location.search);
  if (!params.get('pageSize')) {
    params.delete('pageSize');
    params.append('pageSize', 10);
  }

  const columns = useMemo(() => {
    // Column meta mirrors the V2 Transactions table (tabs/transactionsV2/.../
    // transactions/TransactionsTable.js): fixed px widths, right-aligned
    // numeric + currency columns, cells return raw values (no redundant
    // <span> wrappers — DataTable renders directly into <td>). V1-specific
    // bits preserved: accessor keys (`transactionId / latestDate /
    // transaction / transactionTotal` — legacy data shape), DATETIME_LOCAL_MS
    // parse on the "Ordered on" column, CPC-vs-non-CPC branch on Clicks/
    // Impressions.
    const orderCol = {
      id: 'transactionId',
      accessorKey: 'transactionId',
      header: 'Order',
      sortable: true,
      sortingFn: (a, b) => Number(a.original.transactionId) - Number(b.original.transactionId),
      meta: { align: 'left', width: 160 },
      cell: ({ getValue }) => {
        const v = getValue();
        if (!v) return <span className="campaign-overview__creatives-name">N/A</span>;
        return (
          <Tooltip overlayClassName="mjl-ds" title={v}>
            <span
              className="campaign-overview__creatives-name"
              style={{
                display: 'block',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {v}
            </span>
          </Tooltip>
        );
      },
    };

    const lastImpressionCol = {
      id: 'latestDate',
      accessorKey: 'latestDate',
      header: 'Last Impression',
      sortable: true,
      sortingFn: (a, b) => {
        const x = a.original.latestDate;
        const y = b.original.latestDate;
        if (!x && !y) return 0;
        if (!x) return 1;
        if (!y) return -1;
        return moment(x).valueOf() - moment(y).valueOf();
      },
      meta: { align: 'left', width: 180 },
      cell: ({ getValue }) => {
        const d = getValue();
        return d ? moment(d).format('MM/DD/YY HH:mm') : '';
      },
    };

    const impressionsOrClicks =
      type === 'CPC'
        ? {
            id: 'totalClicks',
            accessorKey: 'totalClicks',
            header: 'Clicks',
            sortable: true,
            sortingFn: (a, b) =>
              (parseInt(a.original.totalClicks) || 0) -
              (parseInt(b.original.totalClicks) || 0),
            meta: { align: 'right', width: 120 },
            cell: ({ getValue }) => getValue() || 0,
          }
        : {
            id: 'totalImpressions',
            accessorKey: 'totalImpressions',
            header: 'Impressions',
            sortable: true,
            sortingFn: (a, b) =>
              (parseInt(a.original.totalImpressions) || 0) -
              (parseInt(b.original.totalImpressions) || 0),
            meta: { align: 'right', width: 120 },
            cell: ({ getValue }) => getValue() || 0,
          };

    const orderedOnCol = {
      id: 'transaction',
      accessorKey: 'transaction',
      header: 'Ordered on',
      sortable: true,
      sortingFn: (a, b) =>
        moment(a.original.transaction, moment.DATETIME_LOCAL_MS).unix() -
        moment(b.original.transaction, moment.DATETIME_LOCAL_MS).unix(),
      meta: { align: 'left', width: 160 },
      cell: ({ getValue }) =>
        moment(getValue(), moment.DATETIME_LOCAL_MS).format('MM/DD/YY HH:mm'),
    };

    const totalCol = {
      id: 'transactionTotal',
      accessorKey: 'transactionTotal',
      header: 'Total',
      sortable: true,
      sortingFn: (a, b) =>
        Number(a.original.transactionTotal || 0) - Number(b.original.transactionTotal || 0),
      meta: { align: 'right', width: 120 },
      cell: ({ getValue }) => {
        const total = getValue();
        const safe = total && !isNaN(total) ? total : 0;
        return formatCurrency2SigFig(safe);
      },
    };

    const attributionCol = {
      id: 'attributionMethod',
      accessorKey: 'attributionMethod',
      header: '',
      sortable: false,
      meta: { align: 'center', width: 70 },
      cell: ({ getValue }) => {
        const method = resolveAttributionMethod(getValue());
        return method ? <AttributionIcon method={method} /> : null;
      },
    };

    return [
      orderCol,
      ...(type === 'CPC' ? [] : [lastImpressionCol]),
      impressionsOrClicks,
      orderedOnCol,
      totalCol,
      attributionCol,
    ];
  }, [type]);

  // Client-side date filtering preserved from V1.
  const rows = useMemo(() => {
    const filtered = dateTrue
      ? transactionData.filter(
          (obj) =>
            moment(obj.transaction).format('YYYY/MM/DD') >= dateFilter[0] &&
            moment(obj.transaction).format('YYYY/MM/DD') <= dateFilter[1]
        )
      : transactionData;
    return (filtered || []).map((item, i) => ({ ...item, __key: i }));
  }, [transactionData, dateTrue, dateFilter]);

  const totalCount = isSimplified
    ? transactionTotalRecord
    : (transactionData && transactionData.length) || 0;

  const handlePaginationChange = ({ pageIndex, pageSize: newPageSize }) => {
    if (setCurrentPage) setCurrentPage(pageIndex + 1);
    if (isSimplified && setCurrentIdx) {
      setCurrentIdx(Number(newPageSize) * Math.abs(pageIndex));
      if (params.get('pageSize')) params.delete('pageSize');
      if (newPageSize > 10) params.append('pageSize', newPageSize);
      history.push(`?${params.toString()}`);
    }
  };

  const renderExpandedRow = (record) => {
    if (isSimplified) {
      return (
        <TransactionImpressions
          reportName={reportName}
          transactionId={record.transactionId}
          campaignOrderId={campaignOrderId}
        />
      );
    }
    const { impressions, clicks } = record;
    return type === 'CPC' ? (
      <ClicksSubTable clicks={clicks || []} />
    ) : (
      <ImpressionSubTable impressions={impressions || []} publisherInfo={publisherInfo} />
    );
  };

  // Mobile card — stacks the row contents into a card so the table is
  // scannable without horizontal scroll on phones / narrow tablets. Header:
  // Order ID + Attribution icon. Then ordered-on timestamp. Then metric
  // chips (Total / Impressions or Clicks / Last Impression).
  const renderMobileCard = (record) => {
    const method = resolveAttributionMethod(record.attributionMethod);
    const total =
      record.transactionTotal && !isNaN(record.transactionTotal)
        ? record.transactionTotal
        : 0;
    const isCPC = type === 'CPC';
    return (
      <>
        <div className="campaign-overview__transactions-card-head">
          {record.transactionId ? (
            <Tooltip overlayClassName="mjl-ds" title={record.transactionId}>
              <div className="campaign-overview__transactions-card-id">
                {record.transactionId}
              </div>
            </Tooltip>
          ) : (
            <div className="campaign-overview__transactions-card-id">N/A</div>
          )}
          {method && <AttributionIcon method={method} />}
        </div>

        <div className="campaign-overview__transactions-card-time">
          {record.transaction
            ? moment(record.transaction, moment.DATETIME_LOCAL_MS).format(
                'MMM DD, YYYY · HH:mm'
              )
            : '—'}
        </div>

        <div className="campaign-overview__transactions-card-chips">
          <span className="campaign-overview__transactions-card-chip">
            <span className="campaign-overview__transactions-card-chip-label">Total</span>
            <span className="campaign-overview__transactions-card-chip-value">
              {formatCurrency2SigFig(total)}
            </span>
          </span>
          <span className="campaign-overview__transactions-card-chip">
            <span className="campaign-overview__transactions-card-chip-label">
              {isCPC ? 'Clicks' : 'Impressions'}
            </span>
            <span className="campaign-overview__transactions-card-chip-value">
              {(isCPC ? record.totalClicks : record.totalImpressions) || 0}
            </span>
          </span>
          {!isCPC && (
            <span className="campaign-overview__transactions-card-chip">
              <span className="campaign-overview__transactions-card-chip-label">
                Last impression
              </span>
              <span className="campaign-overview__transactions-card-chip-value">
                {record.latestDate ? moment(record.latestDate).format('MMM DD, HH:mm') : '—'}
              </span>
            </span>
          )}
        </div>
      </>
    );
  };

  return (
    <DataTable
      data={rows}
      columns={columns}
      pageSize={10}
      pageSizeOptions={[10, 20, 50, 100]}
      totalRows={totalCount}
      pageIndex={(currentPage || 1) - 1}
      onPaginationChange={handlePaginationChange}
      manualPagination={Boolean(isSimplified)}
      mobileBreakpoint="md"
      renderMobileCard={renderMobileCard}
      className="campaign-transactions-v1__transactions-table"
      expandable={{
        isRowExpandable: (record) =>
          isSimplified
            ? true
            : type === 'CPC'
            ? (record.clicks || []).length > 0
            : (record.totalImpressions || 0) > 0,
        renderExpandedRow,
      }}
      loading={loading}
      emptyState="No transactions match the current filter."
    />
  );
};

export default TransactionsTable;
